import type { Locale } from '../i18n';

export type InterviewKind = 'workspace' | 'project' | 'organization';
export type InterviewStatus = 'active' | 'complete' | 'abandoned';
export type InterviewMessageRole = 'system' | 'assistant' | 'user';
export type InterviewMessageKind = 'question' | 'answer' | 'clarification';
export type InterviewGoalStatus = 'open' | 'in_progress' | 'done';

export interface Goal {
  id: string;
  title: string;
  rationale: string;
  done_criteria: string;
  status: InterviewGoalStatus;
  sub_goals?: Goal[];
}

export interface InterviewExtract {
  topic: string;
  purpose: string;
  problem: string;
  solution: string;
  scope: string;
  goals: Goal[];
  risks: string[];
  next_steps: string[];
}

export const EMPTY_EXTRACT: InterviewExtract = {
  topic: '',
  purpose: '',
  problem: '',
  solution: '',
  scope: '',
  goals: [],
  risks: [],
  next_steps: [],
};

export interface InterviewSeed {
  name: string;
  idea: string;
  industry?: string;
  location?: string;
  teamSize?: string;
  stage?: string;
  goals?: string;
  startingMaterial?: string;
}

export interface InterviewMessage {
  id: string;
  sessionId: string;
  role: InterviewMessageRole;
  kind: InterviewMessageKind;
  content: string;
  meta?: Record<string, unknown>;
  createdAt: string;
}

export interface InterviewSession {
  id: string;
  userId: string;
  kind: InterviewKind;
  workspaceId: string;
  parentSessionId?: string | null;
  projectId?: string | null;
  status: InterviewStatus;
  brief: string;
  direction?: string | null;
  workingDoc: string;
  extract: InterviewExtract;
  topic: string;
  locale: Locale;
  seed?: InterviewSeed;
  createdAt: string;
  updatedAt: string;
}

// The shape of an answer/op result the gateway can return.
export interface InterviewOpResult {
  session: InterviewSession;
  question?: string;
  clarification?: string;
  workingDoc?: string;
  extract?: InterviewExtract;
  isDone?: boolean;
  failedStep?: 'update' | 'next' | 'clarify' | 'nextQuestion' | 'regenerate' | 'start';
  error?: string;
  code?: 'PLAN_LIMIT' | 'UPGRADE_REQUIRED' | 'TIMEOUT' | 'BAD_REQUEST';
}

export type InterviewOp = 'start' | 'next' | 'clarify' | 'answer' | 'regenerate' | 'end';

export interface InterviewStartRequest {
  kind: InterviewKind;
  seed: InterviewSeed;
  parentSessionId?: string;
  projectId?: string;
  direction?: string;
  locale: Locale;
}

export interface InterviewAnswerRequest {
  sessionId: string;
  text: string;
  direction?: string;
}

export interface InterviewClarifyRequest {
  sessionId: string;
  questionId?: string;
}

export interface InterviewRegenerateRequest {
  sessionId: string;
  direction?: string;
}

export interface InterviewEndRequest {
  sessionId: string;
  reason?: 'complete' | 'abandoned';
}
